/** The value-entry rules shared by creating a condition (the search box's value stage)
 *  and editing one (ChipEditor), so a value typed in either place commits the same
 *  condition. */
import { equalityOperator } from './operators';
import { quote, unquoteIfWrapped } from './serializer';
import type { FilterValue, NamespaceConfig, QueryOperator } from './types';

export interface TypedValue {
  op: string;
  value: FilterValue;
}

/** A typed value for `op` as the condition it commits, or null when it can't commit
 *  yet (empty, or a range without its `..`). Quotes the user typed are stripped
 *  (re-quoting happens at serialisation). For a range, an empty side becomes the
 *  open-ended `gt` (`v..`) or `lt` (`..v`) where the namespace declares it. */
export function parseTypedValue(
  op: string,
  text: string,
  operators: readonly string[],
): TypedValue | null {
  const t = text.trim();
  if (!t) return null;
  if (op !== 'range') return { op, value: unquoteIfWrapped(t) };
  const idx = t.indexOf('..');
  if (idx < 0) return null;
  const lo = unquoteIfWrapped(t.slice(0, idx).trim());
  const hi = unquoteIfWrapped(t.slice(idx + 2).trim());
  if (lo && hi) return { op: 'range', value: [lo, hi] };
  if (lo && operators.includes('gt')) return { op: 'gt', value: lo };
  if (hi && operators.includes('lt')) return { op: 'lt', value: hi };
  return null;
}

/** The text a committed value reads as in a value input — what parseTypedValue reads
 *  back to the same condition. */
export function valueToText(op: string, value: FilterValue): string {
  if (op === 'range' && Array.isArray(value)) return `${quote(value[0])}..${quote(value[1])}`;
  return Array.isArray(value) ? value[0] : value;
}

/** The operators offered for a namespace: its declared operators, except `exact` where
 *  it sits beside `iexact` (there it is match-by-ID, reached through the "by ID" action
 *  or the `=#` sigil, never a menu choice). `keep` stays listed even when hidden, so a
 *  chip already using it can keep it. */
export function offeredOperators(cfg: NamespaceConfig, keep?: string): string[] {
  const ops: string[] = cfg.operators.includes('iexact')
    ? cfg.operators.filter((op) => op !== 'exact')
    : [...cfg.operators];
  if (keep && !ops.includes(keep)) ops.push(keep);
  return ops;
}

/** A chip's operator as the namespace understands it. Chips rebuilt from query text
 *  carry the sigil's default (`=` → `iexact`), which an exact-only namespace (a date)
 *  doesn't declare; that `=` means its own equality operator. */
export function chipOperator(op: string, operators: readonly QueryOperator[]): string {
  if (op === 'idmatch' || (operators as readonly string[]).includes(op)) return op;
  if (op === 'iexact' || op === 'exact') return equalityOperator(operators) ?? op;
  return op;
}
